import { ReactNode } from "react";
import { Copy, ExternalLink, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MeetingActivity } from "@/components/agent/drawers/MeetingActivity";
import { BaseSheet } from "@/components/common/BaseSheet";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { useGenieMeeting } from "@/hooks/agent/useGenieMeetings";
import {
  describeMeetingFailure,
  formatMeetingDuration,
  formatMeetingMoment,
  PROVIDER_LABELS,
  STATE_LABELS,
} from "@/lib/agent/genieMeetings";
import { AdminGenieMeeting } from "@/types/agent/genie-meeting";
import { NylasHistorySection } from "./NylasHistorySection";

interface AdminMeetingDetailsSheetProps {
  meeting: AdminGenieMeeting | null;
  onClose: () => void;
}

const SECTION_LABEL = "text-sm font-semibold text-gray-700 mb-3";

const copy = async (value: string, label: string) => {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Couldn't copy");
  }
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="min-w-0">
    <dt className="text-xs text-gray-500">{label}</dt>
    <dd className="text-sm text-gray-900 mt-0.5 break-words">{children}</dd>
  </div>
);

const IdRow = ({ label, value }: { label: string; value: string | number | null | undefined }) => (
  <div className="flex items-center justify-between gap-3 min-h-[44px] border-b border-gray-100 last:border-b-0">
    <span className="text-xs text-gray-500 shrink-0">{label}</span>
    {value === null || value === undefined ? (
      <span className="text-sm text-gray-400">—</span>
    ) : (
      <button
        type="button"
        onClick={() => copy(String(value), label)}
        className="inline-flex items-center gap-1.5 min-w-0 font-mono text-xs text-gray-700 hover:text-genie-primary"
        title={`Copy ${label.toLowerCase()}`}
      >
        <span className="truncate">{value}</span>
        <Copy className="h-3.5 w-3.5 shrink-0" />
      </button>
    )}
  </div>
);

const moment = (iso: string | null) => (iso ? formatMeetingMoment(iso) : "—");

/**
 * Read-only view of one genie meeting for admins: every id needed to trace it
 * (including the Nylas notetaker id), its timing, and the full Activity list,
 * plus Nylas's own history of the notetaker on demand.
 * Deleting or adding notes to knowledge stays on the genie's own page.
 */
export const AdminMeetingDetailsSheet = ({ meeting, onClose }: AdminMeetingDetailsSheetProps) => {
  const { data: detail, isLoading, isError } = useGenieMeeting(meeting?.id);
  const genieName = meeting?.agent_name || "The genie";
  const current = detail ?? meeting;

  return (
    <BaseSheet
      isOpen={!!meeting}
      onClose={onClose}
      title={meeting?.title || (meeting ? PROVIDER_LABELS[meeting.meeting_provider] : "Meeting")}
      description={meeting ? `${genieName} · ${moment(meeting.started_at || meeting.join_time || meeting.created_at)}` : undefined}
      size="lg"
    >
      {meeting && current && (
        <div className="space-y-6">
          <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
            <div className="rounded-[10px] bg-white p-5">
              <span className="font-semibold text-gray-900">{STATE_LABELS[current.state]}</span>
              {current.state === "failed" && (
                <p className="text-sm text-gray-600 mt-2">
                  {describeMeetingFailure(current.failure_reason)}
                  {current.failure_reason && (
                    <span className="block font-mono text-xs text-gray-400 mt-1">{current.failure_reason}</span>
                  )}
                </p>
              )}
            </div>
          </div>

          <section>
            <h3 className={SECTION_LABEL}>Details</h3>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Genie">{meeting.agent_name ?? "—"}</Field>
              <Field label="Owner">
                <span className="block">{meeting.owner_name ?? "—"}</span>
                {meeting.owner_email && <CopyableEmail email={meeting.owner_email} className="text-xs text-gray-500" />}
              </Field>
              <Field label="Platform">{PROVIDER_LABELS[current.meeting_provider]}</Field>
              <Field label="What it's about">{current.title ?? "—"}</Field>
              <Field label="Scheduled for">{moment(current.join_time)}</Field>
              <Field label="Created">{moment(current.created_at)}</Field>
              <Field label="Started">{moment(current.started_at)}</Field>
              <Field label="Ended">{moment(current.ended_at)}</Field>
              <Field label="Length">{formatMeetingDuration(current.duration_secs) ?? "—"}</Field>
              <Field label="Email the notes">{current.email_notes ? "On" : "Off"}</Field>
              <Field label="Knowledge base">{current.document_id ? "Added" : "Not added"}</Field>
              <Field label="Inbox">
                {current.conversation_id ? (
                  <Link
                    to={`/genie-hub/conversations/${current.conversation_id}`}
                    className="text-genie-primary hover:underline"
                  >
                    Open conversation
                  </Link>
                ) : (
                  "—"
                )}
              </Field>
            </dl>
            <a
              href={current.meeting_link}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-1.5 min-h-[44px] text-sm text-genie-primary hover:underline break-all"
            >
              {current.meeting_link}
              <ExternalLink className="h-4 w-4 shrink-0" aria-hidden />
            </a>
          </section>

          <section>
            <h3 className={SECTION_LABEL}>IDs</h3>
            <div className="rounded-xl border border-gray-200 px-4">
              <IdRow label="Meeting" value={meeting.id} />
              <IdRow label="Nylas notetaker" value={detail?.nylas_notetaker_id ?? meeting.nylas_notetaker_id} />
              <IdRow label="Genie" value={meeting.agent_id} />
              <IdRow label="Owner" value={meeting.user_id} />
              <IdRow label="Conversation" value={current.conversation_id} />
              <IdRow label="Knowledge document" value={current.document_id} />
            </div>
          </section>

          {isLoading && (
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-genie-primary" />
              <span className="text-sm text-gray-600">Loading the activity...</span>
            </div>
          )}
          {isError && <p className="text-sm text-red-600">The activity couldn't be loaded.</p>}
          {detail && <MeetingActivity events={detail.events ?? []} genieName={genieName} />}

          {(detail?.nylas_notetaker_id ?? meeting.nylas_notetaker_id) && <NylasHistorySection key={meeting.id} meetingId={meeting.id} />}

          {detail?.summary && (
            <section>
              <h3 className={SECTION_LABEL}>Summary</h3>
              <p className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">{detail.summary}</p>
            </section>
          )}

          {detail && detail.action_items.length > 0 && (
            <section>
              <h3 className={SECTION_LABEL}>Action items</h3>
              <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600">
                {detail.action_items.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </BaseSheet>
  );
};
